import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import { ModalProvider, useModal } from './context/ModalContext';
import { ToastProvider } from './context/ToastContext';
import './App.css';
import Navbar from './component/navbar';
import Footer from './component/Footer';
import ScrollToTop from './component/ScrollToTop';
import AuthModals from './component/auth/AuthModals';
import ProtectedRoute from './pages/user/ProtectedRoute';
import AdminRoute from './component/admin/AdminRoute';
import UserLayout from './pages/user/layouts/UserLayout';
import MemiorLayout from './pages/memior/layout';
import ResetPasswordPage from './component/auth/ResetPasswordPage';

// Public pages
import Home from './pages/public/home';
import Shop from './pages/public/shop';
import ProductListing from './pages/public/shop/ProductListing';
import Blog from './pages/public/blog';
import BlogPost from './pages/public/blog/blogpost';
import Memoirs from './pages/public/memoir';
import CreateMemoir from './pages/public/memoir/CreateMemoirr';
import Contact from './pages/public/Contact';
import MemoryMagnets from './pages/public/MemoryMagnets';
import MemoryMagnetsDetails from './pages/public/MemoryMagnets/details';
import MemoryMagnetEdit from './pages/public/MemoryMagnets/MemoryMagnetEdit';
import LagacyMagnets from './pages/public/LEGACYMAGNET';
import FrameItForwardEdit from './pages/public/FRAMEITFORWARD';
import AudioGiftEdit from './pages/public/audiogift';
import Checkout from './pages/public/checkout/Checkout';
import OrderSuccess from './pages/public/checkout/OrderSuccess';
import MemoryPage from './pages/public/MemoryPage';

// User dashboard pages
import Profile from './pages/user/profile';
import Addresses from './pages/user/profile/Addresses';
import Notification from './pages/user/profile/Notification';
import Security from './pages/user/profile/Security';
import AllOrders from './pages/user/my-orders';
import ProcessingOrders from './pages/user/my-orders/processing-Orders';
import ShippedOrders from './pages/user/my-orders/shipped-Orders';
import DeliveredOrders from './pages/user/my-orders/delivered-Orders';
import CancelledOrders from './pages/user/my-orders/cancelled-Orders';
import FutureGifts from './pages/user/future/future-Gifts';
import MemoryCreation from './component/MemoryCreation/index';
import Logout from './pages/user/logout';
import Support from './pages/user/Support';
import SupportDetailPage from './component/support/SupportDetail';
import MemoirGallery from './pages/user/memoir';
import MemoirDetailPage from './pages/user/memoir/MemoirDetailPage';
import MemoirOverview from './pages/memior/overview/';
import ChapterView from './component/memior/ChapterView';

// Admin pages
import AdminLogin from './pages/admin/auth/AdminLogin';
import AdminDashboard from './pages/admin/dashboard/index';
import ProductCatalog from './pages/admin/productcatalogue';
import AdminFutureGifts from './pages/admin/FutureGifts';
import AdminOrders from './pages/admin/order';
import AdminCustomers from './pages/admin/customers';
import AdminSetting from './pages/admin/settings';
import AdminPayment from './pages/admin/payment';
import AdminSupport from './pages/admin/support';
import MemoirManagement from './pages/admin/memior';
import BlogsTab from './pages/admin/blog';

/* ─────────────────────────────────────────────
   Pages where we should NOT auto-open the auth modal.
   Admin area, memoir editor, checkout (users already
   have a "Sign in to continue" flow there), and
   password reset pages.
   ───────────────────────────────────────────── */
const SUPPRESS_ON_PREFIXES = [
  '/admin',
  '/user/memoir',
  '/checkout',
  '/reset-password',
];

const shouldSuppressOnPath = (pathname) => {
  return SUPPRESS_ON_PREFIXES.some((p) => pathname.startsWith(p));
};

/* ─────────────────────────────────────────────
   The actual auto-prompt timer. Lives inside
   Router so it can use useLocation.
   ───────────────────────────────────────────── */
const AuthAutoPrompt = () => {
  const { isAuthenticated } = useAuth();
  const { activeModal, openModal, closeModal } = useModal();
  const location = useLocation();

  // Keep fresh references inside the interval
  const stateRef = useRef({ isAuthenticated, activeModal, pathname: location.pathname });
  useEffect(() => {
    stateRef.current = {
      isAuthenticated,
      activeModal,
      pathname: location.pathname,
    };
  }, [isAuthenticated, activeModal, location.pathname]);

  useEffect(() => {
    const INTERVAL_MS = 30000; // 30 seconds

    const tick = () => {
      const { isAuthenticated, activeModal, pathname } = stateRef.current;

      // 1. Skip if the user is logged in
      if (isAuthenticated) return;

      // 2. Skip on admin / protected / suppressed routes
      if (shouldSuppressOnPath(pathname)) return;

      // 3. Skip if a modal is already open (don't spam / don't fight with the user)
      if (activeModal) return;

      // 4. Show the sign-in modal
      console.log('⏰ Auto-prompting sign-in (guest, 30s elapsed)');
      openModal('signin', {
        // No redirect — let them stay on the current page after signing in
        redirectTo: pathname,
        autoPrompted: true,
      });
    };

    const id = setInterval(tick, INTERVAL_MS);
    return () => clearInterval(id);
  }, [openModal]);

  return null;
};

/* ─────────────────────────────────────────────
   Layout wrapper (so Navbar/Footer don't re-render
   on every route change from App's own render).
   ───────────────────────────────────────────── */
function AppShell() {
  const location = useLocation();

  const isAdmin = location.pathname.startsWith('/admin');
  const isMemoirEditor = location.pathname.startsWith('/user/memoir');

  return (
    <div className="App min-h-screen bg-[#FCF7EE]">
      <ScrollToTop />

      <AuthModals />
      <AuthAutoPrompt />

      {/* Navbar is only for public/user pages, not admin */}
      {!isAdmin && !isMemoirEditor && <Navbar />}

      <main className={isAdmin ? '' : 'pt-16'}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/memory/:pageSlug" element={<MemoryPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/:productSlug" element={<ProductListing />} />
          <Route path="/shop/:productSlug/collection/:collectionId" element={<ProductListing />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/memoirs" element={<Memoirs />} />
          <Route path="/memoirs/create-memoir" element={<CreateMemoir />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/memory-magnets" element={<MemoryMagnets />} />
          <Route path="/memory-magnets/other-template" element={<MemoryMagnetsDetails />} />
          <Route path="/memory-magnets/edit-template" element={<MemoryMagnetEdit />} />
          <Route path="/legacy-magnets/edit-template" element={<LagacyMagnets />} />
          <Route path="/audio-gifts/edit-template" element={<AudioGiftEdit />} />
          <Route path="/frame-it-Forward-edit/edit-template" element={<FrameItForwardEdit />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/verify" element={<OrderSuccess />} />

          {/* Protected Memoir Routes */}
          <Route
            path="/user/memoir"
            element={
              <ProtectedRoute>
                <MemiorLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/account/memoirs" replace />} />
            <Route path=":memoirId" element={<MemoirOverview />} />
            <Route path=":memoirId/chapter/:chapterId" element={<ChapterView />} />
            <Route path="create" element={<CreateMemoir />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />

          <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/settings" element={<AdminRoute><AdminSetting /></AdminRoute>} />
          <Route path="/admin/payment" element={<AdminRoute><AdminPayment /></AdminRoute>} />
          <Route path="/admin/support" element={<AdminRoute><AdminSupport /></AdminRoute>} />
          <Route path="/admin/memoir" element={<AdminRoute><MemoirManagement /></AdminRoute>} />
          <Route path="/admin/frame-it-forward" element={<AdminRoute><AdminFutureGifts /></AdminRoute>} />
          <Route path="/admin/customers" element={<AdminRoute><AdminCustomers /></AdminRoute>} />
          <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
          <Route path="/admin/product-catalog" element={<AdminRoute><ProductCatalog /></AdminRoute>} />
          <Route path="/admin/blog" element={<AdminRoute><BlogsTab /></AdminRoute>} />
          <Route path="/admin/*" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

          {/* Protected User Dashboard Routes */}
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <UserLayout />
              </ProtectedRoute>
            }
          >
            <Route path="profile" element={<Profile />} />
            <Route path="addresses" element={<Addresses />} />
            <Route path="notification" element={<Notification />} />
            <Route path="security" element={<Security />} />
            <Route path="orders/all" element={<AllOrders />} />
            <Route path="orders/processing" element={<ProcessingOrders />} />
            <Route path="orders/shipped" element={<ShippedOrders />} />
            <Route path="orders/delivered" element={<DeliveredOrders />} />
            <Route path="orders/cancelled" element={<CancelledOrders />} />
            <Route path="future-gifts" element={<FutureGifts />} />
            <Route path="future-gifts/create-memory" element={<MemoryCreation />} />
            <Route path="support" element={<Support />} />
            <Route path="support/:slug" element={<SupportDetailPage />} />
          </Route>

          {/* Logout route */}
          <Route path="/logout" element={<Logout />} />

          {/* Redirect any unmatched routes to home */}
          <Route path="*" element={<Home />} />
        </Routes>
      </main>

      {/* Footer is only for public/user pages, not admin */}
      {!isAdmin && !isMemoirEditor && <Footer />}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Top-level providers
   ───────────────────────────────────────────── */
function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <AdminAuthProvider>
          <ModalProvider>
            <Router>
              <AppShell />
            </Router>
          </ModalProvider>
        </AdminAuthProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;